import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useCollapse } from "react-collapsed";

import Avatar from "../../../common/Avatar";
import { Person as PersonIcon, ExitToApp as LogoutIcon } from "../../../common/icons";
import { useGlobalContext } from "../../../contexts/GlobalContext";
import { useAuthToken } from "../../../contexts/AuthTokenContext";
import useNavigationCollapse from "../../../hooks/useNavigationCollapse";
import useRouter from "../../../hooks/useRouter";
import useRoutes from "../../../hooks/useRoutes";
import useTranslate from "../../../hooks/useTranslate";
import { containerOnHover } from "../commonStyles";

const UserOptions = () => {
  const { user, setGlobalContext } = useGlobalContext();
  const { clearToken } = useAuthToken();
  const { getCollapseProps, isExpanded, setExpanded } = useCollapse();
  const { isCollapsed, toggleCollapse } = useNavigationCollapse();
  const { pushRoute } = useRouter();
  const { routes } = useRoutes();
  const i18n = useTranslate();
  const [isHover, setHover] = useState(false);

  const handleClick = () => {
    if (isCollapsed) {
      toggleCollapse();
    }
    setExpanded(!isExpanded);
  };

  useEffect(() => {
    if (isCollapsed && isExpanded) {
      setExpanded(false);
    }
    // Collapsing the nav folds the menu with it. `isExpanded` is deliberately not a
    // dependency: re-running on it would reopen what the user just closed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCollapsed]);

  // Dropping the token is what `useAuthRedirect` watches, so the redirect to /login follows from
  // it. Clearing the user is the other half: the 401 path always did both, and logging out by
  // hand used to drop only the token, leaving the signed-out user's name in the header.
  const handleLogout = () => {
    clearToken();
    setGlobalContext({ user: false });
  };

  if (!user) return null;

  const handleEditProfile = () => {
    setExpanded(false);
    pushRoute(`${routes.users.edit.url}/${user.id}`);
  };

  return (
    <Container>
      <StyledNavLinkRow
        onClick={handleClick}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        $isHover={isHover}
        $isExpanded={isExpanded}
      >
        <StyledAvatar>{user.email.slice(0, 2)}</StyledAvatar>
        <StyledLabel $isCollapsed={isCollapsed}>
          <LabelEmail>{user.email}</LabelEmail>
          {/* The role is shown in the row itself, so no hover bubble is needed. */}
          {user.role && <LabelRole>{user.role}</LabelRole>}
        </StyledLabel>
        <StyledDropdownIcon
          $isCollapsed={isCollapsed}
          className={isExpanded ? "icon-ic-arrow-up" : "icon-ic-arrow-right"}
        />
      </StyledNavLinkRow>

      <div {...getCollapseProps()}>
        <OptionsList>
          <OptionRow onClick={handleEditProfile}>
            <PersonIcon fontSize="small" />
            <span>{i18n("MAIN_MENU.EDIT_PROFILE")}</span>
          </OptionRow>
          <OptionRow onClick={handleLogout}>
            <LogoutIcon fontSize="small" />
            <span>{i18n("MAIN_MENU.LOGOUT")}</span>
          </OptionRow>
        </OptionsList>
      </div>
    </Container>
  );
};

export default UserOptions;

const Container = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.bg.divider};
  align-items: center;
  box-sizing: border-box;
  ${({ theme }) => theme.typography.menuText}
  color: ${({ theme }) => theme.colors.fg.primary};
  text-decoration: none;
  cursor: pointer;
  padding-bottom: 16px;
`;

const StyledNavLinkRow = styled.div<{ $isHover?: boolean; $isExpanded?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px 8px 6px;
  height: 48px;
  box-sizing: border-box;
  border-radius: 8px;
  ${containerOnHover};
  color: ${({ theme }) => theme.colors.fg.primary};
  background: ${({ theme, $isHover, $isExpanded }) =>
    $isHover || $isExpanded ? theme.colors.fg.accent12 : "inherit"};
`;

const StyledAvatar = styled(Avatar)`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
`;

const StyledLabel = styled.span<{ $isCollapsed?: boolean }>`
  display: ${({ $isCollapsed }) => ($isCollapsed ? "none" : "flex")};
  flex-direction: column;
  justify-content: center;
  min-width: 0;
  flex: 1;
`;

const LabelEmail = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const LabelRole = styled.span`
  font-family: "Lato", sans-serif;
  font-size: 11px;
  font-weight: 500;
  line-height: 14px;
  color: ${({ theme }) => theme.colors.fg.secondary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledDropdownIcon = styled.i<{ $isCollapsed?: boolean }>`
  font-size: 24px;
  line-height: 24px;
  text-align: center;
  color: ${({ theme }) => theme.colors.fg.tertiary};
  display: ${({ $isCollapsed }) => ($isCollapsed ? "none" : "block")};
`;

const OptionsList = styled.div`
  padding: 8px 0 0;
`;

const OptionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.txt.label2};
  ${({ theme }) => theme.typography.body2};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.fg.accent12};
    color: ${({ theme }) => theme.colors.fg.accent};

    i,
    svg {
      color: ${({ theme }) => theme.colors.fg.accent};
    }
  }
`;
